import { ViewIds } from '@ardoq/api-types';

export type ViewDataLimitBypass =
  | { kind: 'never' }
  | { kind: 'always' }
  | { kind: 'upToFactorOfLimit'; factor: number };

export type ViewDataLimit = {
  limit: number;
  bypass: ViewDataLimitBypass;
};

export const ARDOQ_GRAPH_COMPONENT_ITEMS_LIMIT = 2000;
export const SPIDER_COMPONENTS_LIMIT = 4000;
export const COMPONENT_MATRIX_COMPONENTS_LIMIT = 4000;
export const COMPONENT_TREE_COMPONENTS_LIMIT = 10000;
export const DEPENDENCY_MAP_ITEMS_LIMIT = 10000;
export const DEPENDENCY_MATRIX_CELLS_LIMIT = 25000000;
export const DEPENDENCY_WHEEL_COMPONENTS_LIMIT = 50;

const BYPASS_ALWAYS: ViewDataLimitBypass = { kind: 'always' };

const ARDOQ_GRAPH_COMPONENT_DATA_LIMIT: ViewDataLimit = {
  limit: ARDOQ_GRAPH_COMPONENT_ITEMS_LIMIT,
  bypass: { kind: 'upToFactorOfLimit', factor: 10 },
};

/**
 * The dataset size each view refuses to render past. A view absent from this
 * registry has no limit and renders whatever it is given. Each limit is counted
 * in the unit its constant is named for; each call site decides for itself
 * whether that count is taken before or after the view model is built.
 */
export const VIEW_DATA_LIMITS: Partial<Record<ViewIds, ViewDataLimit>> = {
  [ViewIds.BLOCK_DIAGRAM]: ARDOQ_GRAPH_COMPONENT_DATA_LIMIT,
  [ViewIds.MODERNIZED_BLOCK_DIAGRAM]: ARDOQ_GRAPH_COMPONENT_DATA_LIMIT,
  [ViewIds.ISOMETRIC_BLOCK_DIAGRAM]: ARDOQ_GRAPH_COMPONENT_DATA_LIMIT,
  [ViewIds.SWIMLANE]: ARDOQ_GRAPH_COMPONENT_DATA_LIMIT,
  [ViewIds.SPIDER]: { limit: SPIDER_COMPONENTS_LIMIT, bypass: BYPASS_ALWAYS },
  [ViewIds.COMPONENT_MATRIX]: {
    limit: COMPONENT_MATRIX_COMPONENTS_LIMIT,
    bypass: BYPASS_ALWAYS,
  },
  [ViewIds.COMPONENT_MATRIX_2]: {
    limit: COMPONENT_MATRIX_COMPONENTS_LIMIT,
    bypass: BYPASS_ALWAYS,
  },
  [ViewIds.COMPONENTTREE]: {
    limit: COMPONENT_TREE_COMPONENTS_LIMIT,
    bypass: BYPASS_ALWAYS,
  },
  [ViewIds.DEPENDENCY_MAP_2]: {
    limit: DEPENDENCY_MAP_ITEMS_LIMIT,
    bypass: BYPASS_ALWAYS,
  },
  [ViewIds.DEPMATRIX]: {
    limit: DEPENDENCY_MATRIX_CELLS_LIMIT,
    bypass: BYPASS_ALWAYS,
  },
  [ViewIds.DEPWHEEL]: {
    limit: DEPENDENCY_WHEEL_COMPONENTS_LIMIT,
    bypass: { kind: 'never' },
  },
};

/** Set on a view's state when its dataset was refused before being processed. */
export type ViewDataLimitExceeded = {
  itemCount: number;
  limit: number;
};

export const getViewDataLimit = (viewId: ViewIds): ViewDataLimit | null =>
  VIEW_DATA_LIMITS[viewId] ?? null;

export const isAboveViewDataLimit = (
  viewId: ViewIds,
  itemCount: number
): boolean => {
  const dataLimit = getViewDataLimit(viewId);
  if (!dataLimit) {
    return false;
  }
  return itemCount > dataLimit.limit;
};

export const canBypassViewDataLimit = (
  viewId: ViewIds,
  itemCount: number
): boolean => {
  const dataLimit = getViewDataLimit(viewId);
  if (!dataLimit) {
    return false;
  }
  switch (dataLimit.bypass.kind) {
    case 'never':
      return false;
    case 'always':
      return true;
    case 'upToFactorOfLimit':
      return itemCount < dataLimit.limit * dataLimit.bypass.factor;
  }
};

type ExceededViewDataLimitArgs = {
  viewId: ViewIds;
  itemCount: number;
  isPresentationMode: boolean;
  hasUserBypassed: boolean;
};

export const getExceededViewDataLimit = ({
  viewId,
  itemCount,
  isPresentationMode,
  hasUserBypassed,
}: ExceededViewDataLimitArgs): ViewDataLimitExceeded | null => {
  if (isPresentationMode) {
    return null;
  }
  const dataLimit = getViewDataLimit(viewId);
  if (!dataLimit || itemCount <= dataLimit.limit) {
    return null;
  }
  if (hasUserBypassed && canBypassViewDataLimit(viewId, itemCount)) {
    return null;
  }
  return { itemCount, limit: dataLimit.limit };
};
